import { gql, useMutation } from '@apollo/client';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from 'hephaestus/web';
import { Refresh } from 'hephaestus/webIcons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SEND_INTEGRATION_FORM_PACK = gql`
  mutation sendIntegrationFormPack($input: SendIntegrationFormPackInput!) {
    sendIntegrationFormPack(input: $input) {
      integrationFormPack {
        id
      }
    }
  }
`;

export const SendIntegrationFormPack = ({
  onNext,
  onBack,
  workflowCompletion,
  direction,
}: {
  onNext: () => void;
  onBack: () => void;
  workflowCompletion: any;
  direction: 'FORWARD' | 'BACKWARD';
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { workflowData } = useContext(WorkflowContext);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const onBackRef = useRef(onBack);
  const hasAttemptedRef = useRef(false);

  onBackRef.current = onBack;

  const [sendIntegrationFormPack] = useMutation(SEND_INTEGRATION_FORM_PACK, {
    onCompleted: () => {
      setError(null);
      onNext();
    },
  });

  const attemptSendIntegrationFormPack = useCallback(async () => {
    if (
      !workflowCompletion.organization?.id ||
      !workflowData.integrationFormPackId
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const variables = {
      input: {
        integrationFormPackId: workflowData.integrationFormPackId,
      },
      skipErrorHandler: true,
    };

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await sendIntegrationFormPack({ variables });
        return;
      } catch (err) {
        if (attempt === 3) {
          setError(err as Error);
        }
      }
    }
    setIsLoading(false);
  }, [workflowCompletion, workflowData, sendIntegrationFormPack]);

  useEffect(() => {
    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;
    if (direction === 'BACKWARD') {
      onBackRef.current();
      return;
    }
    void attemptSendIntegrationFormPack();
  }, [attemptSendIntegrationFormPack, direction]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        minHeight: '300px',
      }}
    >
      <Typography variant='headingMedium'>
        {t('workflowCompletions:sendIntegrationFormPack.title')}
      </Typography>
      <Typography variant='bodyMedium' sx={{ mb: 4, fontWeight: 500 }}>
        {t('workflowCompletions:sendIntegrationFormPack.description')}
      </Typography>
      {error && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Alert severity='error'>{error.message}</Alert>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              onClick={attemptSendIntegrationFormPack}
              disabled={isLoading}
              variant='text'
              startIcon={<Refresh />}
            >
              {t('shared:retry')}
            </Button>
          </Box>
        </Box>
      )}
      {isLoading && <CircularProgress />}
    </Box>
  );
};
